import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import useSWR from 'swr';
import { createUseStyles } from 'react-jss';
import {
    FormControl,
    InputAdornment,
    Card as MuiCard,
    CardContent,
    IconButton,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
} from '@mui/material';
import { ColorLens, Close } from '@mui/icons-material';

import GridContainer from '../../components/Grid/GridContainer.js';
import GridItem from '../../components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SingleImageUpload from 'components/SingleImageUpload/SingleImageUpload';
import CustomInput from 'components/CustomInput/CustomInput.js';
import defaultImage from 'assets/img/placeholder.jpg';
import ColorPicker from 'components/ColorPicker/ColorPicker';

import SparaLoader from 'components/SparaLoader/SparaLoader.js';
import { groupApi, fetcher } from 'lib/api';
import api from 'lib/api';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import SparaSwitch from 'components/SparaSwitch/SparaSwitch';
import { FlexContainer } from 'components/FlexContainer/FlexContainer.jsx';
import ListSelect from 'components/ListSelect/ListSelect';
import CardLayoutPreview from 'components/CardLayoutPreview/CardLayoutPreview';
import GroupHrIntegration from 'components/GroupHrIntegration/GroupHrIntegration';

const style = {
    selectFormControl: {
        margin: '0 0 0px 0',
        position: 'relative',
        '& + $selectFormControl': {
            marginTop: '14px',
        },
    },
    selectLabel: {
        color: '#AAAAAA',
        fontSize: '14px',
        textTransform: 'uppercase',
        fontWeight: '400',
        lineHeight: '1.42857',
        top: '10px',
        left: '0',
        position: 'absolute',
    },
    selectMenu: {
        '& > div > ul': {
            border: '0',
            padding: '5px 0',
            margin: '0',
            fontSize: '14px',
            textAlign: 'left',
            listStyle: 'none',
            backgroundColor: '#fff',
            backgroundClip: 'padding-box',
        },
    },
    select: {
        margin: '0',
        fontSize: '14px',
        lineHeight: '1.42857',
        color: '#555',
        backgroundColor: '#fff',
        padding: '12px 0',
        '&[aria-owns] + input': {
            display: 'none !important',
        },
    },
    colorPickerIcon: {
        position: 'relative',
        cursor: 'pointer',
        color: '#de2c49',
        '& svg': {
            color: '#de2c49',
        },
    },
    attach_group_ids: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginTop: '20px',
    },
    tagItem: {
        backgroundColor: '#de2c49',
        fontSize: '14px',
        borderRadius: '30px',
        height: '30px',
        padding: '0 4px 0 1rem',
        display: 'inline-flex',
        alignItems: 'center',
        margin: '0 0.3rem 0.3rem 0',
        color: '#fff',
        fontWeight: '500',
    },
    button: {
        backgroundColor: 'white',
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        border: 'none',
        cursor: 'pointer',
        fontSize: '20px',
        marginLeft: '10px',
        fontWeight: '500',
        padding: '0',
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    pageContainer: {
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '0 20px',
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    cardContainer: {
        position: 'relative',
    },
    sectionCardBody: {
        display: 'flex',
        flexDirection: 'column',
        padding: '20px',
        gap: 16,
    },
    sectionLabel: {
        margin: '0 0 8px 0',
        fontSize: '13px',
        fontWeight: 600,
        fontStyle: 'italic',
        color: 'var(--spara-text-muted, #666)',
    },
    sectionHeading: {
        marginLeft: '10px',
        marginTop: '20px',
        marginBottom: '8px',
    },
    sectionHeadingTitle: {
        margin: 0,
        lineHeight: 1.15,
        fontWeight: 'bold',
        fontStyle: 'italic',
        color: 'var(--spara-primary, #de2c49)',
    },
    sectionHeadingSubtitle: {
        margin: '1px 0 0',
        lineHeight: 1.2,
        fontSize: '13px',
        fontWeight: 400,
        fontStyle: 'italic',
        color: 'var(--spara-primary, #de2c49)',
    },
    segmented: {
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        marginBottom: 12,
    },
    colorRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    colorSwatch: {
        width: 40,
        height: 40,
        borderRadius: 10,
        border: '1px solid var(--spara-border, #ddd)',
        cursor: 'pointer',
        flexShrink: 0,
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    },
    logoActions: {
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        marginTop: 4,
    },
    headingHint: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginTop: 8,
        flexWrap: 'wrap',
    },
    headingHintText: {
        fontSize: '13px',
        color: 'var(--spara-text-muted, #666)',
    },
    backgroundUpload: {
        maxWidth: 420,
    },
    inputContainer: {
        marginBottom: '24px',
        '& .MuiInputBase-root': {
            padding: '12px 0',
        },
    },
    attachedGroupCard: {
        position: 'relative',
        width: '100%',
        maxWidth: '150px',
        margin: '0',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
    },
    attachedGroupLogo: {
        width: '100%',
        paddingTop: '56.7%', // 1/1.7646768867 * 100
        position: 'relative',
        '& img': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
        },
    },
    attachedGroupContent: {
        padding: '8px !important',
        textAlign: 'center',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    attachedGroupName: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#333',
        marginBottom: '4px',
        display: '-webkit-box',
        '-webkit-line-clamp': 2,
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: '1.2',
    },
    attachedGroupDelete: {
        position: 'absolute',
        top: '2px',
        right: '2px',
        padding: '4px',
        backgroundColor: 'rgba(255,255,255,0.9)',
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,1)',
        },
        '& .MuiSvgIcon-root': {
            fontSize: '16px',
        },
    },
    attachedGroupsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
        gap: '16px',
        marginTop: '20px',
    },
    modalCheckbox: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        zIndex: 1,
        '& .MuiSvgIcon-root': {
            color: '#de2c49',
        },
    },
};

const useStyles = createUseStyles(style);

const AddGroup = ({ t }) => {
    const history = useNavigate();
    const { state } = useLocation();
    const { id } = useParams();
    const classes = useStyles();
    const [isSaving, setIsSaving] = useState(false);
    const isDirtyRef = useRef(false);
    const cardLogoInputRef = useRef(null);
    const [blockedUrl, setBlockedUrl] = useState(null);
    const [showNavBlocker, setShowNavBlocker] = useState(false);

    const markDirty = (val) => {
        isDirtyRef.current = val;
    };

    const readImageFile = (file, onDone) => {
        if (!file) return;
        const extension = file.name.split('.').pop().toLowerCase();
        if (!['jpg', 'jpeg', 'png'].includes(extension)) {
            alert('Skráin verður að vera af gerðinni jpg, jpeg eða png.');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => onDone(reader.result);
        reader.readAsDataURL(file);
    };

    const setCardBackground = (next) => {
        markDirty(true);
        setFormState((prev) => ({
            ...prev,
            background: next,
            ...(next === 'color' && !prev.card_colour
                ? { card_colour: prev.card_colour_default || '#4c535a' }
                : {}),
        }));
    };
    const isUpdating = id !== undefined && id !== 'new';

    let store = JSON.parse(localStorage.getItem('user'));
    const userPermission = JSON.parse(localStorage.getItem('user')).permission;
    const userRoles = store && store.roles;
    const isSuperAdmin = !!(userRoles && userRoles.includes('super_admin'));

    // If is new group and user has no permission to create groups, redirect to groups page
    if (!isUpdating && !userPermission.includes('special_company-create')) {
        history('/groups');
    }

    const [formState, setFormState] = useState({
        alert: '',
        id: null,
        name: '',
        nameState: '',
        display_name: '',
        kennitala: '',
        logo: null,
        hidden: false,
        card_logo: null,
        card_logo_location: 'bottom_right',
        card_colour: '',
        card_colour_default: '#4c535a',
        card_image: null,
        has_membership_card: false,
        has_card_scanning: false,
        allow_member_photo: false,
        show_member_photo_on_card: false,
        member_photo_location: 'top_right',
        member_text_location: 'bottom_left',
        member_text_size: 3.7,
        card_heading_size: 7,
        member_photo_size: 18,
        card_logo_size: 18,
        show_card_heading: false,
        show_card_logo: true,
        card_heading_location: 'top_left',
        type: 'regular',
        background: 'image',
        font_colour: '',
        font_colour_default: '#4c535a',
        card_heading: 'Félagsskírteini',
        card_subtitle: '',
        attached_group_ids: [],
        auto_approve_offers: false,
        visible_to_members: true,
    });

    const {
        data: group,
        error: groupError,
        isLoading: isLoadingGroup,
        mutate: mutateGroup,
    } = useSWR(isUpdating ? groupApi.getById(id) : null, fetcher, { revalidate: false });

    const {
        data: groups,
        error: groupsError,
        isLoading: isLoadingGroups,
        mutate: mutateGroups,
    } = useSWR(groupApi.list(), fetcher);

    const canSeeHrIntegration =
        isSuperAdmin ||
        (isUpdating &&
            !!group?.results &&
            Object.prototype.hasOwnProperty.call(group.results, 'hr_integration'));

    const [openGroupModal, setOpenGroupModal] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const hrIntegrationRef = useRef(null);

    React.useEffect(() => {
        if (group?.results) {
            const attachedGroups = group.results.attached_groups
                ? group.results.attached_groups.map((item) => ({
                      id: item.attached_group_id,
                      name: item.attached_group?.name || '',
                      logo: item.attached_group?.logo || '',
                  }))
                : [];

            setFormState({
                ...formState,
                id: group.results.id,
                name: group.results.name,
                nameState: group.results.name ? 'success' : '',
                display_name: group.results.display_name || '',
                kennitala: group.results.kennitala || '',
                logo: group.results.logo,
                hidden: group.results.hidden || false,
                card_logo: group.results.card_logo || null,
                card_logo_location: group.results.card_logo_location || '',
                card_colour: group.results.card_colour || '',
                card_colour_default: group.results.card_colour || '#4c535a',
                card_image: group.results.card_image,
                has_membership_card: group.results.has_membership_card || false,
                has_card_scanning: group.results.has_card_scanning || false,
                allow_member_photo: group.results.allow_member_photo || false,
                show_member_photo_on_card: group.results.show_member_photo_on_card || false,
                member_photo_location:
                    group.results.member_photo_location || 'top_right',
                member_text_location:
                    group.results.member_text_location || 'bottom_left',
                member_text_size: Number(group.results.member_text_size) || 3.7,
                card_heading_size: Number(group.results.card_heading_size) || 7,
                member_photo_size: Number(group.results.member_photo_size) || 18,
                card_logo_size: Number(group.results.card_logo_size) || 18,
                show_card_heading: !!group.results.show_card_heading,
                show_card_logo:
                    group.results.show_card_logo !== undefined
                        ? !!group.results.show_card_logo
                        : true,
                card_heading_location:
                    group.results.card_heading_location || 'top_left',
                card_logo_location:
                    group.results.card_logo_location || 'bottom_right',
                type: group.results.type || 'regular',
                background: group.results.card_colour
                    ? 'color'
                    : group.results.card_image
                    ? 'image'
                    : 'image',
                font_colour: group.results.font_colour || '',
                font_colour_default: group.results.font_colour || '#4c535a',
                card_heading: group.results.card_heading || 'Félagsskírteini',
                card_subtitle: group.results.card_subtitle || '',
                attached_group_ids: attachedGroups,
                auto_approve_offers: group.results.auto_approve_offers || false,
                visible_to_members:
                    group.results.visible_to_members !== undefined
                        ? group.results.visible_to_members
                        : true,
            });
        }
    }, [group]);

    const verifyLength = (value, length) => {
        return value.length >= length;
    };

    const change = (stateName, data, type, stateNameEqualTo) => {
        markDirty(true);
        if (type === 'length') {
            if (verifyLength(data, stateNameEqualTo)) {
                setFormState({ ...formState, [stateName + 'State']: 'success' });
            } else {
                setFormState({ ...formState, [stateName + 'State']: 'error' });
            }
        }
        setFormState((prevState) => ({
            ...prevState,
            [stateName]: data,
            [stateName + 'State']:
                type === 'length' && verifyLength(data, stateNameEqualTo)
                    ? 'success'
                    : prevState[stateName + 'State'],
        }));
    };

    // Soft hyphen er ósýnilegt í textareit; ¬ sýnir hvar það er sett.
    const SOFT_HYPHEN = '\u00AD';
    const SOFT_HYPHEN_DISPLAY = '¬';
    const toCardHeadingDisplay = (value) =>
        (value || '').split(SOFT_HYPHEN).join(SOFT_HYPHEN_DISPLAY);
    const fromCardHeadingDisplay = (value) =>
        (value || '').split(SOFT_HYPHEN_DISPLAY).join(SOFT_HYPHEN);

    const insertCardHeadingSoftHyphen = () => {
        const input = document.getElementById('card_heading');
        const display = toCardHeadingDisplay(formState.card_heading || '');
        const start =
            input && typeof input.selectionStart === 'number'
                ? input.selectionStart
                : display.length;
        const end =
            input && typeof input.selectionEnd === 'number'
                ? input.selectionEnd
                : display.length;
        const nextDisplay =
            display.slice(0, start) + SOFT_HYPHEN_DISPLAY + display.slice(end);
        change('card_heading', fromCardHeadingDisplay(nextDisplay));
        requestAnimationFrame(() => {
            if (!input) {
                return;
            }
            input.focus();
            const pos = start + SOFT_HYPHEN_DISPLAY.length;
            input.setSelectionRange(pos, pos);
        });
    };

    const changeGroupType = (data) => {
        markDirty(true);
        if (data === 'lite') {
            setFormState({
                ...formState,
                has_membership_card: false,
                has_card_scanning: false,
                allow_member_photo: false,
                show_member_photo_on_card: false,
                member_photo_location: 'top_right',
                member_text_location: 'bottom_left',
                member_text_size: 3.7,
                card_heading_size: 7,
                member_photo_size: 18,
                card_logo_size: 18,
                show_card_heading: false,
                show_card_logo: true,
                card_heading_location: 'top_left',
                card_logo_location: 'bottom_right',
                hidden: true,
                type: data,
            });
        } else {
            setFormState({ ...formState, type: data });
        }
    };

    const handleChangeAttachGroup = (data) => {
        markDirty(true);
        let tempGroupIds = [...formState.attached_group_ids];
        tempGroupIds.push(data.target.value);
        setFormState({ ...formState, attached_group_ids: tempGroupIds });
    };

    const handleDeleteAttachedGroup = (data) => {
        markDirty(true);
        let tempGroupIds = [...formState.attached_group_ids];
        const currentIndex = tempGroupIds.findIndex((f) => f.id === data.id);
        if (currentIndex >= 0) {
            tempGroupIds.splice(currentIndex, 1);
        }
        setFormState({ ...formState, attached_group_ids: tempGroupIds });
    };

    const isValidated = () => {
        if (formState.nameState === 'success') {
            return true;
        } else {
            setFormState({ ...formState, nameState: 'error' });
        }
        return false;
    };

    const hideAlert = () => {
        setFormState({ ...formState, alert: null });
    };

    const changeColor = (state, color) => {
        markDirty(true);
        setFormState((prev) => ({
            ...prev,
            [state]: color,
            alert: null,
            ...(state === 'card_colour' ? { background: 'color' } : {}),
        }));
    };

    const chooseColor = (stateName) => {
        setFormState({
            ...formState,
            alert: (
                <ColorPicker
                    confirm={(color) => changeColor(stateName, color)}
                    onCancel={() => hideAlert()}
                />
            ),
        });
    };

    const onSubmit = async () => {
        if (!isValidated()) return;

        setIsSaving(true);
        const formattedData = {
            alert: '',
            id: formState.id,
            name: formState.name,
            nameState: 'success',
            display_name: formState.display_name,
            kennitala: formState.kennitala,
            logo: formState.logo,
            hidden: formState.hidden,
            card_logo: formState.card_logo,
            card_logo_location: formState.card_logo_location || 'bottom_right',
            card_colour:
                formState.background === 'color'
                    ? formState.card_colour || formState.card_colour_default || '#4c535a'
                    : '',
            card_colour_default:
                formState.background === 'color'
                    ? formState.card_colour || formState.card_colour_default || '#4c535a'
                    : formState.card_colour_default || '#4c535a',
            card_image: formState.background === 'image' ? formState.card_image : null,
            has_membership_card: formState.has_membership_card,
            has_card_scanning:
                formState.has_membership_card && formState.has_card_scanning,
            allow_member_photo:
                formState.has_membership_card && formState.allow_member_photo,
            show_member_photo_on_card:
                formState.has_membership_card &&
                formState.allow_member_photo &&
                formState.show_member_photo_on_card,
            member_photo_location: formState.member_photo_location || 'top_right',
            member_text_location: formState.member_text_location || 'bottom_left',
            member_text_size: formState.member_text_size || 3.7,
            card_heading_size: formState.card_heading_size || 7,
            member_photo_size: formState.member_photo_size || 18,
            card_logo_size: formState.card_logo_size || 18,
            show_card_heading: !!formState.show_card_heading,
            show_card_logo: !!formState.show_card_logo,
            card_heading_location: formState.card_heading_location || 'top_left',
            type: formState.type,
            background: formState.background || (formState.card_image ? 'image' : 'color'),
            font_colour: formState.font_colour || '#000000',
            font_colour_default: formState.font_colour || '#000000',
            card_heading: formState.card_heading || 'Félagsskírteini',
            card_subtitle: formState.card_subtitle || '',
            attached_group_ids:
                formState.attached_group_ids.length > 0
                    ? formState.attached_group_ids.map((item) => item.id).join(',')
                    : '',
            auto_approve_offers: formState.auto_approve_offers,
            visible_to_members: formState.visible_to_members,
        };

        if (isUpdating && userRoles.includes('super_admin')) {
            formattedData.hr_integration = hrIntegrationRef.current
                ? hrIntegrationRef.current.getPayload()
                : undefined;
        }

        try {
            if (isUpdating) {
                await api.put(groupApi.update(id), formattedData);
                await mutateGroup();
                await mutateGroups();
                markDirty(false);
                setIsSaving(false);
            } else {
                await api.post(groupApi.create, formattedData);
                await mutateGroups();
                markDirty(false);
                history('/groups');
            }
        } catch (error) {
            console.error('Error saving group:', error);
            setIsSaving(false);
        }
    };

    const handleOpenGroupModal = () => {
        setSelectedGroups(formState.attached_group_ids);
        setOpenGroupModal(true);
    };

    const handleCloseGroupModal = () => {
        setOpenGroupModal(false);
    };

    const handleGroupSelect = (group) => {
        setSelectedGroups((prev) => {
            if (prev.find((g) => g.id === group.id)) {
                return prev.filter((g) => g.id !== group.id);
            }
            return [...prev, group];
        });
    };

    const handleModalDone = () => {
        markDirty(true);
        setFormState((prev) => ({
            ...prev,
            attached_group_ids: selectedGroups,
        }));
        handleCloseGroupModal();
    };

    useEffect(() => {
        const originalPushState = window.history.pushState;
        let isInternal = false;

        window.history.pushState = function (state, title, url) {
            if (isInternal || !isDirtyRef.current) {
                originalPushState.call(this, state, title, url);
                return;
            }
            try {
                const next = new URL(url, window.location.origin);
                if (next.pathname === window.location.pathname) {
                    originalPushState.call(this, state, title, url);
                    return;
                }
            } catch { /* ignore */ }
            setBlockedUrl(url?.toString() ?? null);
            setShowNavBlocker(true);
        };

        const handlePopState = () => {
            if (!isDirtyRef.current) return;
            isInternal = true;
            originalPushState.call(window.history, null, '', window.location.href);
            isInternal = false;
            setBlockedUrl(null);
            setShowNavBlocker(true);
        };

        const handleBeforeUnload = (e) => {
            if (!isDirtyRef.current) return;
            e.preventDefault();
            e.returnValue = '';
        };

        window.addEventListener('popstate', handlePopState);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.history.pushState = originalPushState;
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const confirmLeave = useCallback(() => {
        markDirty(false);
        setShowNavBlocker(false);
        if (blockedUrl) history(blockedUrl);
    }, [blockedUrl, history]);

    const stayOnPage = useCallback(() => {
        setShowNavBlocker(false);
        setBlockedUrl(null);
    }, []);

    if (isLoadingGroup || isLoadingGroups) return <SparaLoader text={t('common.loading')} />;
    // If code is 404 or id is not a number, show not found page
    if (groupError?.response?.data.code === 404 || (isNaN(parseInt(id)) && isUpdating)) {
        return (
            <div className={classes.pageContainer}>
                <GridContainer>
                    <GridItem xs={12}>
                        <Card>
                            <CardBody>
                                <Typography
                                    variant="h4"
                                    color="error"
                                    style={{ textAlign: 'center', padding: '20px' }}
                                >
                                    {t('group.not_found')}
                                </Typography>
                            </CardBody>
                        </Card>
                    </GridItem>
                </GridContainer>
            </div>
        );
    }

    console.log(formState);
    console.log(formState.background);
    console.log(userPermission);

    return (
        <div className={classes.pageContainer}>
            <GridContainer>
                <GridItem xs={12}>
                    <SparaTitle
                        title={isUpdating ? t('group.update') : t('group.add_new')}
                        rightComponent={
                            <SparaButton color="primary" onClick={onSubmit} disabled={isSaving}>
                                {t('save')}
                            </SparaButton>
                        }
                    />
                    <h3
                        style={{
                            marginLeft: '10px',
                            marginBottom: '5px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            color: 'var(--spara-primary, #de2c49)',
                        }}
                    >
                        {t('group.basic_info')}
                    </h3>
                    <div className={classes.cardContainer}>
                        <Card style={{ margin: '0px' }}>
                            <CardBody>
                                <GridContainer justifyContent="space-between">
                                    {formState.alert}

                                    {/* Grunnupplýsingar */}
                                    <GridItem xs={12} style={{ marginBottom: '20px' }}>
                                        <GridContainer>
                                            <GridItem xs={12} sm={4}>
                                                <SingleImageUpload
                                                    image={formState.logo}
                                                    onUpload={(data) => change('logo', data)}
                                                    onDelete={() => change('logo', null)}
                                                    onError={() => change('logo', null)}
                                                    uploadText={t('group.choose_logo')}
                                                    aspectRatio={1.7646768867}
                                                />
                                            </GridItem>
                                            <GridItem xs={12} sm={8}>
                                                <GridContainer>
                                                    <GridItem
                                                        xs={12}
                                                        sm={6}
                                                        className={classes.inputContainer}
                                                    >
                                                        <CustomInput
                                                            success={
                                                                formState.nameState === 'success'
                                                            }
                                                            error={formState.nameState === 'error'}
                                                            labelText={
                                                                <span>
                                                                    {t('name')}{' '}
                                                                    <small> {t('required')} </small>
                                                                </span>
                                                            }
                                                            id="name"
                                                            formControlProps={{ fullWidth: true }}
                                                            inputProps={{
                                                                value: formState.name,
                                                                onChange: (event) =>
                                                                    change(
                                                                        'name',
                                                                        event.target.value,
                                                                        'length',
                                                                        3
                                                                    ),
                                                            }}
                                                        />
                                                    </GridItem>
                                                    <GridItem
                                                        xs={12}
                                                        sm={6}
                                                        className={classes.inputContainer}
                                                    >
                                                        <CustomInput
                                                            labelText={
                                                                <span>
                                                                    {t('group.display_name')}
                                                                </span>
                                                            }
                                                            id="display_name"
                                                            formControlProps={{ fullWidth: true }}
                                                            inputProps={{
                                                                value: formState.display_name,
                                                                placeholder: formState.name,
                                                                onChange: (event) =>
                                                                    change(
                                                                        'display_name',
                                                                        event.target.value
                                                                    ),
                                                            }}
                                                        />
                                                    </GridItem>
                                                    <GridItem
                                                        xs={12}
                                                        sm={6}
                                                        className={classes.inputContainer}
                                                    >
                                                        <CustomInput
                                                            labelText={
                                                                <span>{t('kennitala')}</span>
                                                            }
                                                            id="kennitala"
                                                            formControlProps={{ fullWidth: true }}
                                                            inputProps={{
                                                                value: formState.kennitala,
                                                                onChange: (event) =>
                                                                    change(
                                                                        'kennitala',
                                                                        event.target.value
                                                                    ),
                                                            }}
                                                        />
                                                    </GridItem>
                                                </GridContainer>
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                </GridContainer>
                            </CardBody>
                        </Card>
                    </div>
                </GridItem>
                {/* Hópategund */}
                <GridItem xs={12}>
                    <h3
                        style={{
                            marginLeft: '10px',
                            marginBottom: '5px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            color: 'var(--spara-primary, #de2c49)',
                        }}
                    >
                        {t('group.type')}
                    </h3>
                    <Card style={{ margin: '0px' }}>
                        <CardBody>
                            <GridContainer>
                                <GridItem xs={12}>
                                    <ListSelect
                                        options={[
                                            {
                                                value: 'regular',
                                                label: t('group.type_regular'),
                                                description: t('group.type_regular_desc'),
                                                color: userPermission.includes(
                                                    'special_company-update_type'
                                                )
                                                    ? 'var(--spara-primary, #de2c49)'
                                                    : 'var(--spara-text-muted, #888)',
                                            },
                                            {
                                                value: 'golden',
                                                label: t('group.type_golden'),
                                                description: t('group.type_golden_desc'),
                                                color: userPermission.includes(
                                                    'special_company-update_type'
                                                )
                                                    ? 'var(--spara-primary, #de2c49)'
                                                    : 'var(--spara-text-muted, #888)',
                                            },
                                            {
                                                value: 'lite',
                                                label: t('group.type_lite'),
                                                description: t('group.type_lite_desc'),
                                                color: userPermission.includes(
                                                    'special_company-update_type'
                                                )
                                                    ? 'var(--spara-primary, #de2c49)'
                                                    : 'var(--spara-text-muted, #888)',
                                            },
                                        ]}
                                        selected={formState.type}
                                        onChange={changeGroupType}
                                        disabled={
                                            !userPermission.includes('special_company-update_type')
                                        }
                                    />
                                </GridItem>
                            </GridContainer>
                        </CardBody>
                    </Card>
                </GridItem>
                {/* Valkostir */}
                <GridItem xs={12}>
                    <h3
                        style={{
                            marginLeft: '10px',
                            marginBottom: '5px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            color: 'var(--spara-primary, #de2c49)',
                        }}
                    >
                        {t('group.options')}
                    </h3>
                    <Card style={{ margin: '0px' }}>
                        <CardBody
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '20px',
                                gap: 10,
                            }}
                        >
                            <FlexContainer style={{ alignItems: 'center', gap: 10 }}>
                                <SparaSwitch
                                    label={t('group.visible')}
                                    subtitle={
                                        formState.hidden
                                            ? t('group.hidden_info')
                                            : t('group.visible_info')
                                    }
                                    checked={!formState.hidden}
                                    onChange={(e) => change('hidden', !e.target.checked)}
                                    disabled={formState.type && formState.type === 'lite'}
                                    labelProps={{
                                        onClick: () =>
                                            !(formState.type && formState.type === 'lite') &&
                                            change('hidden', !formState.hidden),
                                    }}
                                />
                            </FlexContainer>

                            <FlexContainer style={{ alignItems: 'center', gap: 10 }}>
                                <SparaSwitch
                                    label={t('group.show_card')}
                                    subtitle={
                                        formState.has_membership_card
                                            ? t('group.show_card_info')
                                            : t('group.hide_card_info')
                                    }
                                    checked={formState.has_membership_card}
                                    onChange={(e) =>
                                        change('has_membership_card', e.target.checked)
                                    }
                                    disabled={formState.type && formState.type === 'lite'}
                                    labelProps={{
                                        onClick: () =>
                                            !(formState.type && formState.type === 'lite') &&
                                            change(
                                                'has_membership_card',
                                                !formState.has_membership_card
                                            ),
                                    }}
                                />
                            </FlexContainer>

                            {formState.has_membership_card && (
                                <FlexContainer style={{ alignItems: 'center', gap: 10 }}>
                                    <SparaSwitch
                                        label={t('group.card_scanning')}
                                        subtitle={
                                            userRoles.includes('super_admin')
                                                ? formState.has_card_scanning
                                                    ? t('group.card_scanning_info')
                                                    : t('group.card_scanning_disabled_info')
                                                : t('group.card_scanning_contact_us')
                                        }
                                        checked={formState.has_card_scanning}
                                        onChange={(e) =>
                                            change('has_card_scanning', e.target.checked)
                                        }
                                        disabled={!userRoles.includes('super_admin')}
                                        labelProps={{
                                            onClick: () =>
                                                userRoles.includes('super_admin') &&
                                                change(
                                                    'has_card_scanning',
                                                    !formState.has_card_scanning
                                                ),
                                        }}
                                    />
                                </FlexContainer>
                            )}

                            {formState.has_membership_card && (
                                <FlexContainer style={{ alignItems: 'center', gap: 10 }}>
                                    <SparaSwitch
                                        label={t('group.allow_member_photo')}
                                        subtitle={
                                            userRoles.includes('super_admin')
                                                ? formState.allow_member_photo
                                                    ? t('group.allow_member_photo_info')
                                                    : t('group.allow_member_photo_disabled_info')
                                                : t('group.allow_member_photo_contact_us')
                                        }
                                        checked={formState.allow_member_photo}
                                        onChange={(e) => {
                                            const enabled = e.target.checked;
                                            markDirty(true);
                                            setFormState((prev) => ({
                                                ...prev,
                                                allow_member_photo: enabled,
                                                show_member_photo_on_card: enabled
                                                    ? prev.show_member_photo_on_card
                                                    : false,
                                                member_photo_location:
                                                    enabled &&
                                                    prev.member_photo_location ===
                                                        prev.member_text_location
                                                        ? prev.member_text_location ===
                                                          'bottom_right'
                                                            ? 'bottom_left'
                                                            : 'bottom_right'
                                                        : prev.member_photo_location,
                                            }));
                                        }}
                                        disabled={!userRoles.includes('super_admin')}
                                        labelProps={{
                                            onClick: () => {
                                                if (!userRoles.includes('super_admin')) return;
                                                markDirty(true);
                                                setFormState((prev) => {
                                                    const enabled = !prev.allow_member_photo;
                                                    return {
                                                        ...prev,
                                                        allow_member_photo: enabled,
                                                        show_member_photo_on_card: enabled
                                                            ? prev.show_member_photo_on_card
                                                            : false,
                                                        member_photo_location:
                                                            enabled &&
                                                            prev.member_photo_location ===
                                                                prev.member_text_location
                                                                ? prev.member_text_location ===
                                                                  'bottom_right'
                                                                    ? 'bottom_left'
                                                                    : 'bottom_right'
                                                                : prev.member_photo_location,
                                                    };
                                                });
                                            },
                                        }}
                                    />
                                </FlexContainer>
                            )}

                            {userRoles.includes('super_admin') && (
                                <FlexContainer style={{ alignItems: 'center', gap: 10 }}>
                                    <SparaSwitch
                                        label={t('group.auto_approve_offers')}
                                        subtitle={
                                            formState.auto_approve_offers
                                                ? t('group.auto_approve_offers_info')
                                                : t('group.manual_approve_offers_info')
                                        }
                                        checked={formState.auto_approve_offers}
                                        onChange={(e) =>
                                            change('auto_approve_offers', e.target.checked)
                                        }
                                        labelProps={{
                                            onClick: () =>
                                                change(
                                                    'auto_approve_offers',
                                                    !formState.auto_approve_offers
                                                ),
                                        }}
                                    />
                                </FlexContainer>
                            )}

                            <FlexContainer style={{ alignItems: 'center', gap: 10 }}>
                                <SparaSwitch
                                    label={t('group.visible_to_members')}
                                    subtitle={
                                        formState.visible_to_members
                                            ? t('group.visible_to_members_info')
                                            : t('group.hidden_from_members_info')
                                    }
                                    checked={formState.visible_to_members}
                                    onChange={(e) => change('visible_to_members', e.target.checked)}
                                    labelProps={{
                                        onClick: () =>
                                            change(
                                                'visible_to_members',
                                                !formState.visible_to_members
                                            ),
                                    }}
                                />
                            </FlexContainer>
                        </CardBody>
                    </Card>
                </GridItem>
                {formState.type === 'golden' && (() => {
                    const canEdit = userPermission.includes('special_company-update_connected_groups');
                    return (
                        <GridItem xs={12}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-end',
                                    marginBottom: '5px',
                                }}
                            >
                                <h3
                                    style={{
                                        marginLeft: '10px',
                                        fontWeight: 'bold',
                                        fontStyle: 'italic',
                                        color: '#de2c49',
                                        marginBottom: '0px',
                                    }}
                                >
                                    {t('group.attached_companies')}
                                </h3>
                                {canEdit && (
                                    <SparaButton
                                        color="primary"
                                        onClick={handleOpenGroupModal}
                                        style={{ marginRight: '10px', marginBottom: '0px' }}
                                    >
                                        {t('group.select_companies')}
                                    </SparaButton>
                                )}
                            </div>
                            <Card style={{ margin: '0px' }}>
                                <CardBody>
                                    <GridContainer>
                                        <GridItem xs={12} style={{ marginBottom: '20px' }}>
                                            <GridContainer>
                                                <GridItem
                                                    xs={12}
                                                    className={classes.attachedGroupsContainer}
                                                >
                                                    {formState.attached_group_ids.length === 0 ? (
                                                        <div
                                                            style={{
                                                                textAlign: 'center',
                                                                width: '100%',
                                                                padding: '20px',
                                                                gridColumn: '1 / -1',
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body1"
                                                                style={{
                                                                    marginBottom: '10px',
                                                                    fontSize: '1.4rem',
                                                                    color: '#666666',
                                                                }}
                                                            >
                                                                {t('group.no_connected_groups')}
                                                            </Typography>
                                                        </div>
                                                    ) : (
                                                        formState.attached_group_ids
                                                            .sort((a, b) =>
                                                                a.name.localeCompare(b.name)
                                                            )
                                                            .map((item, index) => (
                                                                <MuiCard
                                                                    key={
                                                                        'attach_group_ids_items' +
                                                                        item.id +
                                                                        '_' +
                                                                        index
                                                                    }
                                                                    className={
                                                                        classes.attachedGroupCard
                                                                    }
                                                                >
                                                                    <div
                                                                        className={
                                                                            classes.attachedGroupLogo
                                                                        }
                                                                    >
                                                                        <img
                                                                            src={
                                                                                item.logo ||
                                                                                defaultImage
                                                                            }
                                                                            alt={item.name}
                                                                            onError={(e) => {
                                                                                e.target.onerror = null;
                                                                                e.target.src =
                                                                                    '/spara-placeholder.png';
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    {canEdit && (
                                                                        <IconButton
                                                                            className={
                                                                                classes.attachedGroupDelete
                                                                            }
                                                                            onClick={() =>
                                                                                handleDeleteAttachedGroup(
                                                                                    item
                                                                                )
                                                                            }
                                                                        >
                                                                            <Close />
                                                                        </IconButton>
                                                                    )}
                                                                    <hr
                                                                        style={{
                                                                            width: '100%',
                                                                            border: 'none',
                                                                            borderTop:
                                                                                '1px solid #eee',
                                                                            margin: '0',
                                                                        }}
                                                                    />
                                                                    <CardContent
                                                                        className={
                                                                            classes.attachedGroupContent
                                                                        }
                                                                    >
                                                                        <Typography
                                                                            className={
                                                                                classes.attachedGroupName
                                                                            }
                                                                        >
                                                                            {item.name}
                                                                        </Typography>
                                                                    </CardContent>
                                                                </MuiCard>
                                                            ))
                                                    )}
                                                </GridItem>
                                            </GridContainer>
                                        </GridItem>
                                    </GridContainer>
                                </CardBody>
                            </Card>
                        </GridItem>
                    );
                })()}
                <GridItem xs={12}>
                    {formState.has_membership_card && (
                        <>
                            <h3
                                style={{
                                    marginLeft: '10px',
                                    marginBottom: '5px',
                                    fontWeight: 'bold',
                                    fontStyle: 'italic',
                                    color: 'var(--spara-primary, #de2c49)',
                                }}
                            >
                                {t('group.card')}
                            </h3>
                            <Card style={{ margin: '0px' }}>
                                <CardBody className={classes.sectionCardBody}>
                                    {formState.allow_member_photo && (
                                        <SparaSwitch
                                            label={t('group.show_member_photo_on_card')}
                                            subtitle={
                                                userRoles.includes('super_admin')
                                                    ? formState.show_member_photo_on_card
                                                        ? t(
                                                              'group.show_member_photo_on_card_info'
                                                          )
                                                        : t(
                                                              'group.hide_member_photo_on_card_info'
                                                          )
                                                    : t(
                                                          'group.show_member_photo_on_card_contact_us'
                                                      )
                                            }
                                            checked={formState.show_member_photo_on_card}
                                            onChange={(e) =>
                                                change(
                                                    'show_member_photo_on_card',
                                                    e.target.checked
                                                )
                                            }
                                            disabled={!userRoles.includes('super_admin')}
                                            labelProps={{
                                                onClick: () =>
                                                    userRoles.includes('super_admin') &&
                                                    change(
                                                        'show_member_photo_on_card',
                                                        !formState.show_member_photo_on_card
                                                    ),
                                            }}
                                        />
                                    )}
                                    <SparaSwitch
                                        label={t('group.show_card_heading')}
                                        subtitle={
                                            formState.show_card_heading
                                                ? t('group.show_card_heading_info')
                                                : t('group.hide_card_heading_info')
                                        }
                                        checked={formState.show_card_heading}
                                        onChange={(e) =>
                                            change('show_card_heading', e.target.checked)
                                        }
                                        labelProps={{
                                            onClick: () =>
                                                change(
                                                    'show_card_heading',
                                                    !formState.show_card_heading
                                                ),
                                        }}
                                    />
                                    {formState.show_card_heading && (
                                        <div>
                                            <CustomInput
                                                labelText={
                                                    <span>{t('group.card_heading')}</span>
                                                }
                                                id="card_heading"
                                                formControlProps={{ fullWidth: true }}
                                                inputProps={{
                                                    placeholder: 'Félagsskírteini',
                                                    value: toCardHeadingDisplay(
                                                        formState.card_heading || ''
                                                    ),
                                                    style: {
                                                        fontSize: '24px',
                                                        fontWeight: 'bold',
                                                    },
                                                    onChange: (event) =>
                                                        change(
                                                            'card_heading',
                                                            fromCardHeadingDisplay(
                                                                event.target.value
                                                            )
                                                        ),
                                                }}
                                            />
                                            <div className={classes.headingHint}>
                                                <SparaButton
                                                    variant="outlined"
                                                    color="secondary"
                                                    size="small"
                                                    onClick={insertCardHeadingSoftHyphen}
                                                    type="button"
                                                >
                                                    {t('group.card_heading_soft_hyphen')}
                                                </SparaButton>
                                                <span className={classes.headingHintText}>
                                                    {t('group.card_heading_soft_hyphen_info')}
                                                </span>
                                            </div>
                                            <CustomInput
                                                labelText={
                                                    <span>{t('group.card_subtitle')}</span>
                                                }
                                                id="card_subtitle"
                                                formControlProps={{ fullWidth: true }}
                                                formStyle={{ marginTop: 12 }}
                                                inputProps={{
                                                    placeholder: t(
                                                        'group.card_subtitle_placeholder'
                                                    ),
                                                    value: formState.card_subtitle || '',
                                                    maxLength: 255,
                                                    style: {
                                                        fontSize: '18px',
                                                        fontWeight: 600,
                                                        fontStyle: 'italic',
                                                    },
                                                    onChange: (event) =>
                                                        change(
                                                            'card_subtitle',
                                                            event.target.value
                                                        ),
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <SparaSwitch
                                            label={t('group.show_card_logo')}
                                            subtitle={
                                                formState.show_card_logo
                                                    ? t('group.show_card_logo_info')
                                                    : t('group.hide_card_logo_info')
                                            }
                                            checked={formState.show_card_logo}
                                            onChange={(e) =>
                                                change('show_card_logo', e.target.checked)
                                            }
                                            labelProps={{
                                                onClick: () =>
                                                    change(
                                                        'show_card_logo',
                                                        !formState.show_card_logo
                                                    ),
                                            }}
                                        />
                                        {formState.show_card_logo && (
                                            <div className={classes.logoActions}>
                                                <SparaButton
                                                    variant="outlined"
                                                    color="secondary"
                                                    size="small"
                                                    onClick={() =>
                                                        cardLogoInputRef.current?.click()
                                                    }
                                                >
                                                    {t('group.replace_card_logo')}
                                                </SparaButton>
                                                {formState.card_logo ? (
                                                    <SparaButton
                                                        variant="outlined"
                                                        color="secondary"
                                                        size="small"
                                                        onClick={() => change('card_logo', null)}
                                                    >
                                                        {t('group.reset_card_logo')}
                                                    </SparaButton>
                                                ) : null}
                                                <input
                                                    ref={cardLogoInputRef}
                                                    type="file"
                                                    accept=".jpg,.jpeg,.png"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => {
                                                        readImageFile(e.target.files?.[0], (data) =>
                                                            change('card_logo', data)
                                                        );
                                                        e.target.value = '';
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <p className={classes.sectionLabel}>
                                            {t('group.font_color')}
                                        </p>
                                        <div className={classes.colorRow}>
                                            <button
                                                type="button"
                                                className={classes.colorSwatch}
                                                style={{
                                                    background:
                                                        formState.font_colour || '#ffffff',
                                                }}
                                                onClick={() => chooseColor('font_colour')}
                                                aria-label={t('group.font_color')}
                                            />
                                            <FormControl fullWidth>
                                                <CustomInput
                                                    id="font_colour"
                                                    inputProps={{
                                                        placeholder: '#FFFFFF',
                                                        value: formState.font_colour || '',
                                                        onChange: (event) =>
                                                            change(
                                                                'font_colour',
                                                                event.target.value
                                                            ),
                                                        endAdornment: (
                                                            <InputAdornment
                                                                position="end"
                                                                className={classes.colorPickerIcon}
                                                                onClick={() =>
                                                                    chooseColor('font_colour')
                                                                }
                                                            >
                                                                <ColorLens />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div>
                                        <p className={classes.sectionLabel}>
                                            {t('group.card_background')}
                                        </p>
                                        <div className={classes.segmented}>
                                            <SparaButton
                                                variant={
                                                    formState.background === 'image'
                                                        ? 'filled'
                                                        : 'outlined'
                                                }
                                                color="primary"
                                                size="small"
                                                onClick={() => setCardBackground('image')}
                                            >
                                                {t('group.card_background_image')}
                                            </SparaButton>
                                            <SparaButton
                                                variant={
                                                    formState.background === 'color'
                                                        ? 'filled'
                                                        : 'outlined'
                                                }
                                                color="primary"
                                                size="small"
                                                onClick={() => setCardBackground('color')}
                                            >
                                                {t('group.card_background_color')}
                                            </SparaButton>
                                        </div>
                                        {formState.background === 'image' ? (
                                            <div className={classes.backgroundUpload}>
                                                <SingleImageUpload
                                                    image={formState.card_image}
                                                    onUpload={(data) =>
                                                        change('card_image', data)
                                                    }
                                                    onDelete={() => change('card_image', null)}
                                                    onError={() => change('card_image', null)}
                                                    uploadText={t('group.choose_background')}
                                                    aspectRatio={1.7646768867}
                                                    disableCrop
                                                />
                                            </div>
                                        ) : (
                                            <div className={classes.colorRow}>
                                                <button
                                                    type="button"
                                                    className={classes.colorSwatch}
                                                    style={{
                                                        background:
                                                            formState.card_colour ||
                                                            formState.card_colour_default ||
                                                            '#4c535a',
                                                    }}
                                                    onClick={() => chooseColor('card_colour')}
                                                    aria-label={t('group.choose_colour')}
                                                />
                                                <FormControl fullWidth>
                                                    <CustomInput
                                                        id="card_colour"
                                                        inputProps={{
                                                            placeholder: '#4c535a',
                                                            value: formState.card_colour || '',
                                                            onChange: (event) =>
                                                                change(
                                                                    'card_colour',
                                                                    event.target.value
                                                                ),
                                                            endAdornment: (
                                                                <InputAdornment
                                                                    position="end"
                                                                    className={
                                                                        classes.colorPickerIcon
                                                                    }
                                                                    onClick={() =>
                                                                        chooseColor('card_colour')
                                                                    }
                                                                >
                                                                    <ColorLens />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </FormControl>
                                            </div>
                                        )}
                                    </div>
                                    <CardLayoutPreview
                                        cardImage={formState.card_image}
                                        cardColour={
                                            formState.card_colour ||
                                            formState.card_colour_default ||
                                            '#4c535a'
                                        }
                                        preferColour={formState.background === 'color'}
                                        fontColour={formState.font_colour}
                                        logo={formState.card_logo || formState.logo}
                                        cardHeading={(formState.card_heading || '')
                                            .split('\u00AD')
                                            .join('')
                                            .split('¬')
                                            .join('')}
                                        cardSubtitle={formState.card_subtitle || ''}
                                        textLocation={formState.member_text_location}
                                        photoLocation={formState.member_photo_location}
                                        titleLocation={formState.card_heading_location}
                                        logoLocation={
                                            formState.card_logo_location || 'bottom_right'
                                        }
                                        titleSize={formState.card_heading_size || 7}
                                        photoSize={formState.member_photo_size || 18}
                                        logoSize={formState.card_logo_size || 18}
                                        showPhoto={!!formState.show_member_photo_on_card}
                                        showTitle={!!formState.show_card_heading}
                                        showLogo={
                                            !!formState.show_card_logo &&
                                            !!(formState.card_logo || formState.logo)
                                        }
                                        onTextLocationChange={(corner) =>
                                            change('member_text_location', corner)
                                        }
                                        onPhotoLocationChange={(corner) =>
                                            change('member_photo_location', corner)
                                        }
                                        onTitleLocationChange={(corner) =>
                                            change('card_heading_location', corner)
                                        }
                                        onLogoLocationChange={(corner) =>
                                            change('card_logo_location', corner)
                                        }
                                        onTitleSizeChange={(size) =>
                                            change('card_heading_size', size)
                                        }
                                        onPhotoSizeChange={(size) =>
                                            change('member_photo_size', size)
                                        }
                                        onLogoSizeChange={(size) =>
                                            change('card_logo_size', size)
                                        }
                                        onResetDefaults={() => {
                                            markDirty(true);
                                            setFormState((prev) => ({
                                                ...prev,
                                                member_text_location: 'bottom_left',
                                                member_photo_location: 'top_right',
                                                card_heading_location: 'top_left',
                                                card_logo_location: 'bottom_right',
                                                member_text_size: 3.7,
                                                card_heading_size: 7,
                                                member_photo_size: 18,
                                                card_logo_size: 18,
                                            }));
                                        }}
                                    />
                                </CardBody>
                            </Card>
                        </>
                    )}
                </GridItem>
                {canSeeHrIntegration && (
                    <GridItem xs={12}>
                        <div className={classes.sectionHeading}>
                            <h3 className={classes.sectionHeadingTitle}>
                                {t('group.hr_integration')}
                            </h3>
                            <p className={classes.sectionHeadingSubtitle}>
                                {t('group.hr_integration_info')}
                            </p>
                        </div>
                        <Card style={{ margin: '0px' }}>
                            <CardBody className={classes.sectionCardBody}>
                                <GroupHrIntegration
                                    ref={hrIntegrationRef}
                                    groupId={isUpdating ? id : null}
                                    integration={group?.results?.hr_integration}
                                    isGolden={formState.type === 'golden'}
                                    canEdit={isSuperAdmin}
                                    onDirty={() => markDirty(true)}
                                    onSave={isSuperAdmin ? onSubmit : undefined}
                                    saving={isSaving}
                                    t={t}
                                />
                            </CardBody>
                        </Card>
                    </GridItem>
                )}
                {isSaving && (
                    <div className={classes.overlay}>
                        <SparaLoader text={t('common.saving')} />
                    </div>
                )}
            </GridContainer>

            {isUpdating && (
                <div
                    style={{
                        marginTop: '20px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0 20px',
                    }}
                >
                    <>
                        {userPermission.includes('special_company-delete') ? (
                            <SparaButton
                                color="danger"
                                onClick={async () => {
                                    try {
                                        await api.delete(groupApi.delete(id));
                                        await mutateGroups();
                                        history('/groups');
                                    } catch (error) {
                                        console.error('Error deleting group:', error);
                                    }
                                }}
                            >
                                {t('delete')}
                            </SparaButton>
                        ) : (
                            <div></div>
                        )}
                    </>
                    <SparaButton color="primary" onClick={onSubmit} disabled={isSaving}>
                        {t('save')}
                    </SparaButton>
                </div>
            )}

            <Dialog open={showNavBlocker} maxWidth="xs" fullWidth>
                <DialogTitle>{t('unsaved_changes_title')}</DialogTitle>
                <DialogContent>
                    <p style={{ margin: 0, fontSize: '14px', color: 'var(--spara-text, #333)' }}>
                        {t('unsaved_changes_message')}
                    </p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={stayOnPage}>{t('unsaved_stay')}</Button>
                    <Button onClick={confirmLeave} color="error">
                        {t('unsaved_leave')}
                    </Button>
                    <Button
                        onClick={async () => {
                            await onSubmit();
                            confirmLeave();
                        }}
                        variant="contained"
                        color="success"
                    >
                        {t('save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openGroupModal} onClose={handleCloseGroupModal} maxWidth="md" fullWidth>
                <DialogTitle>{t('group.select_companies')}</DialogTitle>
                <DialogContent>
                    <div className={classes.attachedGroupsContainer}>
                        {groups?.results?.map((group) => (
                            <MuiCard
                                key={group.id}
                                className={classes.attachedGroupCard}
                                onClick={() => handleGroupSelect(group)}
                                style={{
                                    cursor: 'pointer',
                                    border: selectedGroups.find((g) => g.id === group.id)
                                        ? '2px solid #de2c49'
                                        : 'none',
                                }}
                            >
                                <Checkbox
                                    className={classes.modalCheckbox}
                                    checked={
                                        selectedGroups.find((g) => g.id === group.id) !== undefined
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleGroupSelect(group);
                                    }}
                                />
                                <div className={classes.attachedGroupLogo}>
                                    <img
                                        src={group.logo || defaultImage}
                                        alt={group.name}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '/spara-placeholder.png';
                                        }}
                                    />
                                </div>
                                <hr
                                    style={{
                                        width: '100%',
                                        border: 'none',
                                        borderTop: '1px solid #eee',
                                        margin: '0',
                                    }}
                                />
                                <CardContent className={classes.attachedGroupContent}>
                                    <Typography className={classes.attachedGroupName}>
                                        {group.name}
                                    </Typography>
                                </CardContent>
                            </MuiCard>
                        ))}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseGroupModal}>{t('cancel')}</Button>
                    <Button onClick={handleModalDone} color="primary" variant="contained">
                        {t('done')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default withTranslation('', { withRef: true })(AddGroup);
